import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { LanguagePicker } from './LanguagePicker';
import { HistoryTagSearchSection } from './HistoryTagSearchSection';
import { CallTranscriptionSection } from './CallTranscriptionSection';
import { LoginLimitSection } from './LoginLimitSection';
import { ReplyTranslateLanguagesSection } from './ReplyTranslateLanguagesSection';
import { caughtErrorMessage, serverErrorMessage } from '../i18n/server-errors';
import { t } from '../services/language-manager';
import { registerResumeDraft, resumeDraft } from '../services/reauth-resume';
import { getOrgSettings, saveOrgSettings } from '../services/wizard-backend';
import { BackButton } from './BackButton';
import { SaveButton, SaveRow, useSaveConfirmation } from './SaveFeedback';
import logo from '../assets/mediabriz-logo-svg-400px.svg';

interface SettingsPageProps {
  genesysOrgId: string;
  genesysRegion: string;
  onBack: () => void;
  /** Mediabriz has turned Login Limit on for this organization. */
  loginLimitEnabled?: boolean;
  /** Operator flag; false for a Login Limit only org, which has no general settings. */
  conversationsEnabled?: boolean;
  /** Dev preview: show the form without calling the API. */
  preview?: boolean;
}

// Basic mailbox check — server re-validates on save.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type SettingsTab = 'general' | 'login-limit';

interface ThreatDraft {
  enabled: boolean;
  email: string;
}

const SETTINGS_TABS: readonly SettingsTab[] = ['general', 'login-limit'];

function tabDomId(tab: SettingsTab): string {
  return tab === 'general' ? 'settings-tab-general' : 'settings-tab-login-limit';
}

function panelDomId(tab: SettingsTab): string {
  return tab === 'general' ? 'settings-panel-general' : 'settings-panel-login-limit';
}

/**
 * Organization settings. Guard, secure media delivery, reply translation, and
 * other conversations (external tag search) are the general settings. Login Limit is a second tab only when Mediabriz
 * has enabled it (`loginLimitEnabled`); with that flag off, this page is the
 * general settings and there is no tab bar. The subtitle under the title
 * stays above the tabs and describes the whole page, on either tab.
 * Both panels stay mounted so unsaved edits survive a tab switch.
 * Conversation Actions is its own page.
 * A Login Limit only org (`conversationsEnabled` false) has no general
 * settings: the page is the Login Limit panel alone, with no tab bar.
 * The Guard block renders only when `scanEnabled` is true. The Settings row
 * itself is shown to every admin who can manage settings.
 * See docs/INSTALL_FLOW.md "Settings button vs scanEnabled".
 */
export function SettingsPage({
  genesysOrgId,
  genesysRegion,
  onBack,
  loginLimitEnabled = false,
  conversationsEnabled = true,
  preview = false,
}: SettingsPageProps) {
  const loginLimitOnly = !conversationsEnabled && loginLimitEnabled;
  const showLoginLimitTab = loginLimitEnabled && !loginLimitOnly;
  const [activeTab, setActiveTab] = useState<SettingsTab>(
    () => (resumeDraft<SettingsTab>('settings-tab') === 'login-limit' ? 'login-limit' : 'general'),
  );
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [email, setEmail] = useState('');
  const [mediaDeliveryOn, setMediaDeliveryOn] = useState(false);
  const [scanEnabled, setScanEnabled] = useState(true);
  const [replyLanguages, setReplyLanguages] = useState<string[] | null>(null);
  const [historyTagSearch, setHistoryTagSearch] = useState(false);
  const [callTranscription, setCallTranscription] = useState(true);
  const [callTranscriptExport, setCallTranscriptExport] = useState(true);
  const [saving, setSaving] = useState(false);
  const saveConfirmation = useSaveConfirmation();
  const [saveError, setSaveError] = useState<string | null>(null);
  /** Guard email as last loaded or saved, so only real edits are kept across a sign-in. */
  const loadedThreat = useRef<ThreatDraft | null>(null);
  const kept = useRef({ activeTab, threat: { enabled, email } });
  kept.current = { activeTab, threat: { enabled, email } };

  useEffect(() => {
    const stopTab = registerResumeDraft('settings-tab', () => kept.current.activeTab);
    const stopThreat = registerResumeDraft('settings-threat', () => {
      const loaded = loadedThreat.current;
      const { threat } = kept.current;
      if (!loaded || (loaded.enabled === threat.enabled && loaded.email === threat.email)) return null;
      return threat;
    });
    return () => {
      stopTab();
      stopThreat();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (loginLimitOnly) return;
    setLoading(true);
    if (preview) {
      setEnabled(true);
      setEmail('security@example.com');
      setMediaDeliveryOn(true);
      setReplyLanguages(null);
      setCallTranscription(true);
      setCallTranscriptExport(true);
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const settings = await getOrgSettings(genesysOrgId, genesysRegion);
        if (cancelled) return;
        loadedThreat.current = { enabled: settings.threatNotifyEnabled, email: settings.threatNotifyEmail };
        // Edits kept when the session expired come back unsaved, over the fresh settings.
        const draft = resumeDraft<ThreatDraft>('settings-threat');
        const restored = draft && typeof draft.enabled === 'boolean' && typeof draft.email === 'string' ? draft : null;
        setEnabled(restored ? restored.enabled : settings.threatNotifyEnabled);
        setEmail(restored ? restored.email : settings.threatNotifyEmail);
        setScanEnabled(settings.scanEnabled);
        setMediaDeliveryOn(settings.mediaDeliveryEnabled && settings.scanEnabled);
        setReplyLanguages(settings.replyTranslateLanguages);
        setHistoryTagSearch(settings.historyTagSearchEnabled);
        setCallTranscription(settings.callTranscriptionEnabled);
        setCallTranscriptExport(settings.callTranscriptExportEnabled);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setLoadError(caughtErrorMessage(e, 'settings-load-error'));
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [genesysOrgId, genesysRegion, preview, loginLimitOnly]);

  useEffect(() => {
    if (!showLoginLimitTab) setActiveTab('general');
  }, [showLoginLimitTab]);

  const emailValid = !enabled || EMAIL_RE.test(email.trim());
  const canSave = emailValid && !saving;

  const handleSave = async () => {
    saveConfirmation.clear();
    setSaveError(null);
    if (!canSave) return;
    setSaving(true);
    try {
      const result = await saveOrgSettings(genesysOrgId, genesysRegion, {
        threatNotifyEnabled: enabled,
        threatNotifyEmail: enabled ? email.trim() : '',
      });
      if (result.status) {
        loadedThreat.current = { enabled, email: enabled ? email.trim() : '' };
        saveConfirmation.confirm(t('settings-save-success'));
      } else {
        setSaveError(serverErrorMessage({
          code: result.code,
          cause: result.cause,
          knownKey: result.code ? undefined : 'settings-save-error',
          keepDetail: true,
        }));
      }
    } catch (e) {
      setSaveError(caughtErrorMessage(e, 'settings-save-error'));
    } finally {
      setSaving(false);
    }
  };

  const onTabsKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const rtl = document.documentElement.dir === 'rtl';
    const nextKey = rtl ? 'ArrowLeft' : 'ArrowRight';
    const previousKey = rtl ? 'ArrowRight' : 'ArrowLeft';
    const index = SETTINGS_TABS.indexOf(activeTab);
    let nextIndex = index;
    if (event.key === nextKey) nextIndex = (index + 1) % SETTINGS_TABS.length;
    else if (event.key === previousKey) nextIndex = (index - 1 + SETTINGS_TABS.length) % SETTINGS_TABS.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = SETTINGS_TABS.length - 1;
    else return;
    event.preventDefault();
    const next = SETTINGS_TABS[nextIndex];
    setActiveTab(next);
    document.getElementById(tabDomId(next))?.focus();
  };

  const generalSettings = (
    <>
      {loading && <p className="settings-page__status">{t('settings-loading')}</p>}

      {!loading && loadError && (
        <p className="settings-page__error" role="alert">
          {loadError}
        </p>
      )}

      {!loading && !loadError && scanEnabled && (
        <div className="settings-section settings-section--first">
          <div className="form-group">
            <label className="settings-page__toggle">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => {
                  setEnabled(e.target.checked);
                  saveConfirmation.clear();
                  setSaveError(null);
                }}
              />
              <span>{t('settings-threat-email-enable')}</span>
            </label>
            <p className="form-hint">{t('settings-threat-email-help')}</p>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="threat-notify-email">
              {t('settings-threat-email-label')}
            </label>
            <input
              id="threat-notify-email"
              className="form-input"
              type="email"
              dir="ltr"
              autoComplete="email"
              value={email}
              disabled={!enabled}
              placeholder={t('settings-threat-email-placeholder')}
              onChange={(e) => {
                setEmail(e.target.value);
                saveConfirmation.clear();
                setSaveError(null);
              }}
            />
            {enabled && email.trim() && !emailValid && (
              <p className="form-error">{t('settings-threat-email-invalid')}</p>
            )}
          </div>

          <SaveRow className="settings-section__save" error={saveError} message={saveConfirmation.message}>
            <SaveButton
              saving={saving}
              justSaved={saveConfirmation.justSaved}
              label={t('settings-save')}
              savingLabel={t('settings-saving')}
              onClick={handleSave}
              disabled={!canSave}
            />
          </SaveRow>
        </div>
      )}

      {/* Mediabriz-managed and read-only; hidden rather than shown as "Off". */}
      {!loading && !loadError && mediaDeliveryOn && (
        <div className="settings-section">
          <div className="settings-readonly">
            <span className="settings-readonly__label">{t('settings-media-delivery-label')}</span>
            <span className="settings-readonly__value">{t('settings-media-delivery-status-on')}</span>
          </div>
          <p className="form-hint">{t('settings-media-delivery-help')}</p>
        </div>
      )}

      {!loading && !loadError && (
        <ReplyTranslateLanguagesSection
          genesysOrgId={genesysOrgId}
          genesysRegion={genesysRegion}
          languages={replyLanguages}
          first={!scanEnabled}
          preview={preview}
        />
      )}

      {!loading && !loadError && (
        <HistoryTagSearchSection
          genesysOrgId={genesysOrgId}
          genesysRegion={genesysRegion}
          enabled={historyTagSearch}
          preview={preview}
        />
      )}

      {!loading && !loadError && (
        <CallTranscriptionSection
          genesysOrgId={genesysOrgId}
          genesysRegion={genesysRegion}
          transcriptionEnabled={callTranscription}
          exportEnabled={callTranscriptExport}
          preview={preview}
        />
      )}
    </>
  );

  return (
    <div className="card settings-page">
      <div className="settings-page__nav">
        <BackButton onClick={onBack} />
        <LanguagePicker />
      </div>
      <img src={logo} alt="Mediabriz" className="settings-page__logo" />
      <h2 className="settings-page__title">{t('settings-title')}</h2>
      <p className="settings-page__intro">{t('settings-intro')}</p>

      {showLoginLimitTab && (
        <div
          className="settings-tabs"
          role="tablist"
          aria-label={t('settings-title')}
          onKeyDown={onTabsKeyDown}
        >
          <button
            type="button"
            role="tab"
            id={tabDomId('general')}
            className={`settings-tabs__tab${activeTab === 'general' ? ' settings-tabs__tab--active' : ''}`}
            aria-selected={activeTab === 'general'}
            aria-controls={panelDomId('general')}
            tabIndex={activeTab === 'general' ? 0 : -1}
            onClick={() => setActiveTab('general')}
          >
            {t('settings-tab-general')}
          </button>
          <button
            type="button"
            role="tab"
            id={tabDomId('login-limit')}
            className={`settings-tabs__tab${activeTab === 'login-limit' ? ' settings-tabs__tab--active' : ''}`}
            aria-selected={activeTab === 'login-limit'}
            aria-controls={panelDomId('login-limit')}
            tabIndex={activeTab === 'login-limit' ? 0 : -1}
            onClick={() => setActiveTab('login-limit')}
          >
            {t('settings-tab-login-limit')}
          </button>
        </div>
      )}

      {loginLimitOnly ? (
        <LoginLimitSection
          genesysOrgId={genesysOrgId}
          genesysRegion={genesysRegion}
          first
          threatMailbox={false}
          preview={preview}
        />
      ) : showLoginLimitTab ? (
        <div
          className="settings-page__panel"
          role="tabpanel"
          id={panelDomId('general')}
          aria-labelledby={tabDomId('general')}
          hidden={activeTab !== 'general'}
        >
          {generalSettings}
        </div>
      ) : generalSettings}

      {showLoginLimitTab && (
        <div
          className="settings-page__panel"
          role="tabpanel"
          id={panelDomId('login-limit')}
          aria-labelledby={tabDomId('login-limit')}
          hidden={activeTab !== 'login-limit'}
        >
          <LoginLimitSection
            genesysOrgId={genesysOrgId}
            genesysRegion={genesysRegion}
            first
            preview={preview}
          />
        </div>
      )}
    </div>
  );
}
